/**
 * Extrai a mensagem PT-BR amigável do erro lançado pelo customFetch.
 *
 * Suporta o novo shape padrão da API:
 *   `{ error: { code, message, details? } }`
 *
 * Caso o erro não tenha esse shape (rede caiu, parsing falhou, etc.),
 * cai num fallback genérico amigável.
 */
export function extractApiErrorMessage(
  err: unknown,
  // Padrão (microcopy guideline jun/2026): causa + ação. "Algo deu errado.
  // Tente novamente." é vago — não diz o que fazer. Veja lib/messages.ts.
  fallback = "Não foi possível concluir — tente de novo em instantes.",
): string {
  if (!err) return fallback;

  if (typeof err === "string") {
    const trimmed = err.trim();
    return trimmed || fallback;
  }

  if (typeof err === "object") {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const inner = (data as { error?: unknown }).error;
      if (inner && typeof inner === "object") {
        const message = (inner as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) {
          return message.trim();
        }
      }
      if (typeof inner === "string" && inner.trim()) {
        return inner.trim();
      }
      const topMessage = (data as { message?: unknown }).message;
      if (typeof topMessage === "string" && topMessage.trim()) {
        return topMessage.trim();
      }
    }

    // 413 da borda (nginx acima de 50 MB, Cloudflare acima de 100 MB) chega em HTML, antes da API: sem isto a tela
    // mostrava o começo do HTML. O 413 da própria API (foto acima de 20 MB) tem mensagem e já saiu acima.
    if ((err as { status?: unknown }).status === 413) {
      return "O arquivo é grande demais. Escolha um arquivo menor.";
    }

    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      // ApiError.message vem como "HTTP 400 Bad Request: <mensagem>".
      // Removemos o prefixo HTTP para mostrar só o texto amigável.
      const stripped = message.replace(/^HTTP\s+\d+\s+[^:]*:\s*/i, "").trim();
      return stripped || message.trim();
    }
  }

  return fallback;
}
